import { FC, useCallback, useEffect, useState } from 'react';

import { DownloadOutlined } from '@ant-design/icons';
import { Alert, Select, Tabs } from 'antd';
// import DownloadSwitcher from 'component/download-switcher';
import { useLocation } from 'react-router-dom';
import { useActiveEvent } from 'tools/hooks/useActiveEvent.hook';
import { Button, Label, Modal, Typography } from 'ui';
import BackLink from 'ui/BackLink';
import MemoizedDateTab from 'ui/date-tab';

import { orderFilterSelector } from 'selectors/eventsSlice.selector';
import {
  useLazyExportAttendeesQuery,
  useLazyGetEventDetailQuery,
} from 'services/events/eventsApiService';
import { setActiveEventName } from 'slices/eventsSlice';
import { useAppDispatch, useAppSelector } from 'store/index';

import EventPopover from 'containers/event-popover';

import { UserEventType } from 'types/core/userTypes';

import s from './Orders.module.scss';
// import Attendees from './attendees/eventAttendeesList';
import OrdersList from './orders-list';
import Transactions from './transactions/transactions';

const Orders: FC = () => {
  const dispatch = useAppDispatch();
  const requestFilter = useAppSelector(orderFilterSelector);
  const activeEvent = useActiveEvent() as UserEventType | null;
  const [getEventDetail, { data: eventDetail }] = useLazyGetEventDetailQuery();
  const [exportAttendees] = useLazyExportAttendeesQuery();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const eventId = searchParams.get('event');
  const [isDownLoading, setIsDownLoading] = useState<boolean>();
  const [curDateId, setCurDateId] = useState<string>();
  const [curDateName, setCurDateName] = useState<string>();
  // const [isPDF, setIsPDF] = useState<boolean>(false);
  const [tabKey, setTabKey] = useState<string>('order');
  const [ticketType, setTicketType] = useState<string[]>();

  const [open, setOpen] = useState(false);
  const showModal = () => {
    setOpen(true);
  };

  useEffect(() => {
    dispatch(setActiveEventName(activeEvent?.eventName ?? ''));
  }, [dispatch, activeEvent?.eventName]);

  const handleOk = () => {
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleDownloadAttendees = useCallback(() => {
    setIsDownLoading(true);
    const finalEventId = eventId ?? activeEvent?.eventId;
    if (!finalEventId) return;

    exportAttendees({ EventId: finalEventId, DateId: curDateId, TicketTypeIds: ticketType }).then(
      (data) => {
        if (data.data) {
          const link = document.createElement('a');
          link.href = data.data?.fileName;
          link.setAttribute('download', `attendees-${finalEventId}.csv`);
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setIsDownLoading(false);
        }
      },
    );
  }, [exportAttendees, curDateId, eventId, ticketType, activeEvent?.eventId]);

  useEffect(() => {
    const finalEventId = eventId ?? activeEvent?.eventId;
    if (!finalEventId) return;
    getEventDetail(finalEventId);
  }, [getEventDetail, eventId, activeEvent]);

  const handleTabChange = (key) => {
    setTabKey(key);
  };

  return (
    <div className={s.wrapper}>
      <BackLink />
      <div className={s.row}>
        <div className={s.titileHeader}>
          {eventDetail && (
            <Typography type="h2" weight={700}>
              &quot;{eventDetail?.basicInfo?.eventName ?? ''}&quot;&nbsp;Orders
            </Typography>
          )}
        </div>
        <div className={s.export}>
          <EventPopover />
          <Button
            size="large"
            style={{ fontSize: 13 }}
            loading={isDownLoading}
            type="primary"
            onClick={showModal}>
            <DownloadOutlined />
            EXPORT LIST
          </Button>
        </div>
      </div>

      <Modal
        open={open}
        onOk={handleOk}
        handleClose={handleCancel}
        footer={
          <div
            style={{
              marginLeft: 'auto',
              marginRight: 'auto',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Button
                loading={isDownLoading}
                type="primary"
                size="large"
                onClick={handleDownloadAttendees}
                style={{ fontSize: 13, width: 150 }}>
                <DownloadOutlined />
                DOWNLOAD
              </Button>
              <Button style={{ fontSize: 13, width: 150 }} size="large" onClick={handleCancel}>
                CANCEL
              </Button>
            </div>
            <Label>Please disable all popup blockers</Label>
          </div>
        }>
        <Modal.Header>
          {tabKey === 'order' ? (
            <Typography type="h3" weight={700}>
              Choose file type for Attendee list
            </Typography>
          ) : (
            <Typography type="h3" weight={700}>
              Choose file type for Transaction list
            </Typography>
          )}
          {/* <div style={{ marginTop: 16, marginBottom: 16 }}>
            <DownloadSwitcher value={isPDF} onChange={setIsPDF} />
          </div> */}
        </Modal.Header>
        <Alert message={`You are exporting data for ${curDateName}`} type="warning" />
        <div style={{ marginTop: 24 }}></div>
        {/* {isPDF ? (
          <Typography type="h6">
            PDF is a nicely formatted document showing the name on the ticket and who ordered it.
          </Typography>
        ) : ( */}
        <Typography type="main">
          CSV — {tabKey === 'order' ? 'Attendee' : 'Transaction'} list which includes all important
          fields including, if available, user address, phone numbers, and other information
          requested by event in comma delimited format.
        </Typography>
        {/* )} */}

        <div style={{ marginTop: 16, marginBottom: 24 }}>
          <Label>Ticket type (if you wish to show only specific ticket types)</Label>

          <Select
            style={{ width: '100%' }}
            mode="multiple"
            onChange={(value: string[]) => {
              setTicketType(value);
            }}
            allowClear
            showSearch
            size="large"
            placeholder="Select ticket types"
            options={
              eventDetail?.ticketTypes?.map((item) => ({
                label: item.name,
                value: item.id,
              })) || []
            }
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>
      </Modal>

      <MemoizedDateTab
        initialDateId={requestFilter?.DateId}
        setCurrentDateName={setCurDateName}
        setCurrentDateId={setCurDateId}
        eventId={eventId ?? activeEvent?.eventId}
      />

      <div className={s.list}>
        <Tabs
          defaultActiveKey="order"
          onChange={handleTabChange}
          tabBarStyle={{ color: '#8D949C' }}
          style={{ width: '100%' }}
          items={[
            {
              key: 'order',
              label: (
                <span
                  style={{
                    fontWeight: tabKey === 'order' ? '600' : 'normal',
                    color: tabKey === 'order' ? '#1677ff' : '#8D949C',
                  }}>
                  ALL TICKETS SOLD
                </span>
              ),
              children: (
                <OrdersList
                  ticketTypes={eventDetail?.ticketTypes}
                  curDateId={tabKey === 'order' ? curDateId : undefined}
                  currency={eventDetail?.basicInfo?.currency ?? activeEvent?.currency}
                  eventId={eventId ?? activeEvent?.eventId}
                  eventName={activeEvent?.eventName}
                  flyer={eventDetail?.basicInfo?.eventFlyer ?? ''}
                />
              ),
            },
            {
              key: 'transaction',
              label: (
                <span
                  style={{
                    fontWeight: tabKey === 'transaction' ? '600' : 'normal',
                    color: tabKey === 'transaction' ? '#1677ff' : '#8D949C',
                  }}>
                  TRANSACTIONS
                </span>
              ),
              children:
                eventId ?? activeEvent?.eventId ? (
                  <Transactions
                    curDateId={tabKey === 'transaction' ? curDateId : undefined}
                    eventId={eventId ?? activeEvent?.eventId}
                  />
                ) : null,
            },
          ]}
        />
      </div>
    </div>
  );
};

export default Orders;
